<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import { toast } from 'vue-sonner';
import { CheckCircle2, Copy, ExternalLink, Link2, Mail } from 'lucide-vue-next';
import { copyToClipboard } from '~/lib/clipboard';
import type { TenantForm } from '~/types/models';

const props = defineProps<{
  forms: TenantForm[];
  tenantId: string;
  targetLabel: string;
  portalClientSlug?: string;
  linkedClientId?: string;
  linkedProjectId?: string;
  projectOptions?: Array<{ id: string; name: string }>;
  recipientName?: string;
  recipientEmail?: string;
}>();

const NO_PROJECT_VALUE = '__no_project__';
const selectedFormId = ref('');
const selectedProjectValue = ref('');
const copied = ref(false);
const sendingInvite = ref(false);
const preparingLink = ref(false);
const accessUrl = ref('');
const accessMode = ref<'portal' | 'email_token' | 'public' | ''>('');
const accessDebug = ref<{
  requestedTenantId: string;
  requestedFormId: string;
  resolvedFormId: string;
  resolvedFormSlug: string | null;
  resolvedFormStatus: string | null;
  allowPublicAccessRaw: boolean | string | number | null;
  allowPublicAccessType: string;
  formAllowsPublicAccess: boolean;
  requestedAccessMode: string;
  preferPortalAssignment: boolean;
  portalRequestId: string | null;
  portalClientSlug: string | null;
  forceSecureInvite: boolean;
  forcePublicLink: boolean;
  isPublicForm: boolean;
  accessMode: 'portal' | 'email_token' | 'public';
  deliverEmail: boolean;
  linkedClientId: string | null;
  linkedProjectId: string | null;
  actionLink: string;
} | null>(null);
const inviteRecipientEmail = ref('');
const inviteRecipientName = ref('');
const { $auth } = useNuxtApp();

const sendableForms = computed(() => props.forms
  .filter((form) => form.status === 'published')
  .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))));

watch(sendableForms, (forms) => {
  if (!forms.length) {
    selectedFormId.value = '';
    return;
  }
  if (!forms.some((form) => form.id === selectedFormId.value)) {
    selectedFormId.value = forms[0]?.id || '';
  }
}, { immediate: true });

watch(selectedFormId, () => {
  copied.value = false;
  accessUrl.value = '';
  accessMode.value = '';
  accessDebug.value = null;
  inviteRecipientEmail.value = '';
  inviteRecipientName.value = '';
});

watch(selectedProjectValue, () => {
  copied.value = false;
  accessUrl.value = '';
  accessMode.value = '';
  accessDebug.value = null;
  inviteRecipientEmail.value = '';
  inviteRecipientName.value = '';
});

const normalizedProjectOptions = computed(() => {
  const seen = new Set<string>();
  return (props.projectOptions || [])
    .filter((project) => {
      const id = String(project.id || '').trim();
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .map((project) => ({
      id: String(project.id || '').trim(),
      name: String(project.name || '').trim() || 'Untitled project',
    }));
});

const showProjectSelector = computed(() => normalizedProjectOptions.value.length > 0);
const projectSelectorOptions = computed(() => [
  {
    value: NO_PROJECT_VALUE,
    label: 'Not linked to a project',
    description: 'Keep this form attached to the client record only.',
  },
  ...normalizedProjectOptions.value.map((project) => ({
    value: project.id,
    label: project.name,
    description: 'Attach the submission to this project as well.',
  })),
]);

watch(
  () => [props.linkedProjectId, normalizedProjectOptions.value.map((project) => project.id).join('|')],
  () => {
    if (!showProjectSelector.value) {
      selectedProjectValue.value = props.linkedProjectId || '';
      return;
    }

    const current = String(selectedProjectValue.value || '').trim();
    const currentExists = current === NO_PROJECT_VALUE
      || normalizedProjectOptions.value.some((project) => project.id === current);

    if (currentExists) return;

    const linkedProjectId = String(props.linkedProjectId || '').trim();
    selectedProjectValue.value = linkedProjectId
      && normalizedProjectOptions.value.some((project) => project.id === linkedProjectId)
      ? linkedProjectId
      : NO_PROJECT_VALUE;
  },
  { immediate: true },
);

const resolvedProjectId = computed(() => {
  if (!showProjectSelector.value) return String(props.linkedProjectId || '').trim();
  return selectedProjectValue.value === NO_PROJECT_VALUE ? '' : String(selectedProjectValue.value || '').trim();
});

const sendsTrackedInvite = computed(() => true);
const usesPortalAssignment = computed(() => Boolean(props.portalClientSlug && props.linkedClientId));
const selectedForm = computed(() => sendableForms.value.find((form) => form.id === selectedFormId.value) || null);
const sendableFormOptions = computed(() => sendableForms.value.map((form) => ({
  value: form.id,
  label: form.name,
  description: `${form.allowPublicAccess === true ? 'Public' : 'Secure invite'} • ${form.description || form.introText || 'Published form'}`,
})));

const isSelectedFormPublic = computed(() => {
  if (accessMode.value) return accessMode.value === 'public';
  return sendsTrackedInvite.value ? false : selectedForm.value?.allowPublicAccess === true;
});

const resolvedLink = computed(() => accessUrl.value);
const canSendInvite = computed(() => Boolean(selectedForm.value && props.tenantId));
const canAssignToPortal = computed(() => Boolean(selectedForm.value && props.tenantId && usesPortalAssignment.value));
const hasAccessMismatch = computed(() => {
  if (!selectedForm.value || !accessDebug.value) return false;
  return (selectedForm.value.allowPublicAccess === true) !== accessDebug.value.isPublicForm;
});

async function prepareAccessLink(options?: { deliverEmail?: boolean; silent?: boolean }) {
  if (!selectedForm.value || !props.tenantId) return '';

  const deliverEmail = options?.deliverEmail === true;
  if (deliverEmail) {
    sendingInvite.value = true;
  } else {
    preparingLink.value = true;
  }

  try {
    const token = await $auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('You need to be signed in to create form links.');
    }

    const response = await $fetch<{
      ok: boolean;
      accessUrl: string;
      recipientEmail?: string;
      recipientName?: string;
      portalRequestId?: string;
      accessMode: 'portal' | 'email_token' | 'public';
      deliveryStatus?: 'sent' | 'skipped' | 'not_requested';
      debug?: {
        requestedTenantId: string;
        requestedFormId: string;
        resolvedFormId: string;
        resolvedFormSlug: string | null;
        resolvedFormStatus: string | null;
        allowPublicAccessRaw: boolean | string | number | null;
        allowPublicAccessType: string;
        formAllowsPublicAccess: boolean;
        requestedAccessMode: string;
        preferPortalAssignment: boolean;
        portalRequestId: string | null;
        portalClientSlug: string | null;
        forceSecureInvite: boolean;
        forcePublicLink: boolean;
        isPublicForm: boolean;
        accessMode: 'portal' | 'email_token' | 'public';
        deliverEmail: boolean;
        linkedClientId: string | null;
        linkedProjectId: string | null;
        actionLink: string;
      };
    }>('/api/forms/send-invite', {
      method: 'POST',
      headers: {
        authorization: `Bearer ${token}`,
      },
      body: {
        tenantId: props.tenantId,
        formId: selectedForm.value.id,
        linkedClientId: props.linkedClientId,
        linkedProjectId: resolvedProjectId.value || undefined,
        deliverEmail,
        accessMode: sendsTrackedInvite.value ? 'secure' : undefined,
        preferPortalAssignment: usesPortalAssignment.value,
      },
    });

    accessUrl.value = response.accessUrl;
    accessMode.value = response.accessMode;
    accessDebug.value = response.debug || null;
    inviteRecipientEmail.value = response.recipientEmail || '';
    inviteRecipientName.value = response.recipientName || '';

    if (response.debug && selectedForm.value && (selectedForm.value.allowPublicAccess === true) !== response.debug.isPublicForm) {
      toast.warning(`Share mode mismatch: UI sees ${selectedForm.value.allowPublicAccess === true ? 'public' : 'secure'}, server resolved ${response.debug.isPublicForm ? 'public' : 'secure'}.`);
    }

    if (deliverEmail) {
      if (response.deliveryStatus === 'skipped') {
        toast.success('Email delivery is not configured here yet. The form link is ready to copy and send manually.');
      } else {
        toast.success(
          response.recipientEmail
            ? `Form invite sent to ${response.recipientEmail}.`
            : 'Form invite sent.',
        );
      }
    } else if (response.accessMode === 'portal' && !options?.silent) {
      toast.success('Form added to the client portal.');
    }

    return response.accessUrl;
  } catch (error: any) {
    if (!options?.silent) {
      toast.error(error?.data?.statusMessage || error?.message || 'Unable to prepare the form link right now.');
    }
    return '';
  } finally {
    if (deliverEmail) {
      sendingInvite.value = false;
    } else {
      preparingLink.value = false;
    }
  }
}

async function copyLink() {
  const link = resolvedLink.value || await prepareAccessLink();
  if (!link) return;
  await copyToClipboard(link);
  copied.value = true;
  window.setTimeout(() => {
    copied.value = false;
  }, 1800);
}

async function sendInviteEmail() {
  await prepareAccessLink({ deliverEmail: true });
}

watch(
  () => [selectedForm.value?.id || '', resolvedProjectId.value],
  async ([formId]) => {
    if (!formId || accessUrl.value || preparingLink.value || usesPortalAssignment.value) return;
    await prepareAccessLink({ silent: true });
  },
  { immediate: true },
);
</script>

<template>
  <div class="space-y-5">
    <div class="space-y-2">
      <div class="flex flex-wrap items-center gap-2">
        <Badge variant="outline">Published forms</Badge>
        <Badge variant="secondary">{{ targetLabel }}</Badge>
      </div>
      <p class="text-sm text-muted-foreground">
        {{
          usesPortalAssignment
            ? 'Choose a published form to add to the client portal. Assigned forms stay in the portal until the client completes them, and completed responses remain available there as records.'
            : 'Choose a published form to send. This dialog creates a secure tracked invite link so the response stays attached to this record and can be resumed later.'
        }}
      </p>
    </div>

    <div v-if="sendableForms.length === 0" class="rounded-xl border border-dashed border-border/70 px-4 py-5 text-sm text-muted-foreground">
      No published forms are ready to send yet. Publish a form from the forms workspace first.
    </div>

    <template v-else>
      <div class="space-y-2">
        <Label>Form</Label>
        <SearchableSelectMenu
          v-model="selectedFormId"
          :options="sendableFormOptions"
          placeholder="Choose a form"
          search-placeholder="Search forms..."
          show-all-by-default
          variant="form"
        />
      </div>

      <div v-if="showProjectSelector" class="space-y-2">
        <Label>Attach to project</Label>
        <SearchableSelectMenu
          v-model="selectedProjectValue"
          :options="projectSelectorOptions"
          placeholder="Choose a project"
          search-placeholder="Search linked projects..."
          show-all-by-default
          variant="form"
        />
        <p class="text-xs text-muted-foreground">
          Leave this set to “Not linked to a project” when the form is only about the broader client record.
        </p>
      </div>

      <div v-if="selectedForm" class="rounded-xl border border-border/70 bg-background/70 px-4 py-4">
        <div class="flex flex-wrap items-center gap-2">
          <Badge variant="outline">{{ selectedForm.kind === 'questionnaire' ? 'Questionnaire' : 'Enquiry form' }}</Badge>
          <Badge variant="secondary">{{ usesPortalAssignment ? 'Portal form' : 'Secure invite' }}</Badge>
          <Badge v-if="selectedForm.allowPublicAccess === true" variant="outline">Public form also enabled</Badge>
          <Badge variant="outline">{{ selectedForm.pages.length }} page{{ selectedForm.pages.length === 1 ? '' : 's' }}</Badge>
        </div>
        <p class="mt-2 text-sm font-medium">{{ selectedForm.name }}</p>
        <p class="mt-1 text-sm text-muted-foreground">
          {{ selectedForm.description || selectedForm.introText || 'This form will be delivered through a secure invite link with saved draft progress.' }}
        </p>
      </div>

      <div v-if="!portalClientSlug" class="rounded-xl border border-dashed border-border/70 px-4 py-5 text-sm text-muted-foreground">
        No portal workspace is linked yet, so this form will be sent as a secure email invite and remain editable through that link.
      </div>

      <div v-else class="rounded-xl border border-border/70 bg-background/70 px-4 py-4">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div class="space-y-1 text-sm text-muted-foreground">
            <p class="font-medium text-foreground">Client portal assignment</p>
            <p>Add this form to the client portal so it shows as an action card and stays available in the Forms area until completed.</p>
          </div>
          <Button type="button" :disabled="!canAssignToPortal || preparingLink" @click="prepareAccessLink()">
            <Link2 class="mr-2 size-4" />
            {{ preparingLink ? 'Adding…' : 'Add to portal' }}
          </Button>
        </div>
      </div>

      <div v-if="selectedForm" class="space-y-2">
        <Label>{{ usesPortalAssignment ? 'Portal link' : 'Share link' }}</Label>
        <div v-if="resolvedLink" class="flex flex-col gap-2 md:flex-row md:items-center">
          <code class="min-w-0 flex-1 break-all rounded-xl border border-border/70 bg-background/70 px-3 py-3 text-xs">
            {{ resolvedLink }}
          </code>
          <div class="flex flex-wrap gap-2">
            <Button type="button" variant="outline" @click="copyLink">
              <Copy class="mr-2 size-4" />
              {{ copied ? 'Copied' : 'Copy link' }}
            </Button>
            <Button v-if="resolvedLink" type="button" as-child>
              <a :href="resolvedLink" target="_blank" rel="noopener noreferrer">
                <ExternalLink class="mr-2 size-4" />
                Open form
              </a>
            </Button>
          </div>
        </div>
        <div v-else class="rounded-xl border border-dashed border-border/70 px-4 py-4 text-sm text-muted-foreground">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <p>
              {{
                usesPortalAssignment
                  ? 'Create the portal assignment first, then you can copy or open the direct portal link.'
                  : 'Preparing the secure share link so the copied URL matches the email version.'
              }}
            </p>
            <Button type="button" variant="outline" :disabled="preparingLink" @click="prepareAccessLink()">
              <Link2 class="mr-2 size-4" />
              {{ preparingLink ? 'Preparing…' : (usesPortalAssignment ? 'Create portal link' : 'Generate link') }}
            </Button>
          </div>
        </div>
        <div
          v-if="hasAccessMismatch && accessDebug"
          class="rounded-xl border border-amber-300/70 bg-amber-50/70 px-4 py-4 text-xs text-amber-950"
        >
          <p class="font-medium">Debug: form access mismatch</p>
          <p class="mt-1">
            UI form <code>{{ selectedForm?.id }}</code> is marked
            <strong>{{ selectedForm?.allowPublicAccess === true ? 'public' : 'secure' }}</strong>,
            but the server resolved
            <strong>{{ accessDebug.isPublicForm ? 'public' : 'secure' }}</strong>
            from <code>allowPublicAccess={{ String(accessDebug.allowPublicAccessRaw) }}</code>
            ({{ accessDebug.allowPublicAccessType }}).
          </p>
          <p class="mt-1">
            Server form: <code>{{ accessDebug.resolvedFormId }}</code>
            <span v-if="accessDebug.resolvedFormSlug"> · slug <code>{{ accessDebug.resolvedFormSlug }}</code></span>
          </p>
        </div>
      </div>

      <div class="rounded-xl border border-border/70 bg-background/70 px-4 py-4">
        <div class="flex items-start gap-3">
          <div class="mt-0.5 rounded-full bg-primary/10 p-2 text-primary">
            <Link2 class="size-4" />
          </div>
          <div class="space-y-1 text-sm text-muted-foreground">
            <p class="font-medium text-foreground">Submission linking</p>
            <p>
              When this form is completed, the response will be attached to {{ targetLabel.toLowerCase() }}
              <span v-if="resolvedProjectId"> and the selected project.</span>
            </p>
            <p>
              {{
                usesPortalAssignment
                  ? 'Portal forms stay visible in the client portal until they are submitted, and completed responses stay available there for reference.'
                  : 'Secure invites can be reopened from the same link, saved as draft, and completed later.'
              }}
            </p>
            <p v-if="copied" class="inline-flex items-center gap-1 text-emerald-600">
              <CheckCircle2 class="size-4" />
              Link copied and ready to send.
            </p>
            <p v-if="inviteRecipientEmail" class="text-foreground">
              Sent to {{ inviteRecipientName || inviteRecipientEmail }}.
            </p>
          </div>
        </div>
      </div>

      <div class="rounded-xl border border-border/70 bg-background/70 px-4 py-4">
        <div class="flex items-start justify-between gap-3">
          <div class="space-y-1 text-sm text-muted-foreground">
            <p class="font-medium text-foreground">Email invite</p>
            <p>
              {{
                usesPortalAssignment
                  ? 'Send an email that points the client back into the portal form assignment.'
                  : 'Send a secure email link that lets the recipient open the form, save progress, and return later without portal setup.'
              }}
            </p>
          </div>
          <Button type="button" :disabled="!canSendInvite || sendingInvite" @click="sendInviteEmail">
            <Mail class="mr-2 size-4" />
            {{ sendingInvite ? 'Sending…' : 'Send invite email' }}
          </Button>
        </div>
      </div>
    </template>
  </div>
</template>
